import { useEffect, useState } from 'react';
import {
  Users,
  ShoppingCart,
  CreditCard,
  GlobeHemisphereWest,
} from '@phosphor-icons/react';
import { apiClient } from '@dukene/shared';

interface LiveMetrics {
  activeSessions: number;
  activeCarts: number;
  activeCheckouts: number;
  timestamp: string;
}

export default function LiveView() {
  const [metrics, setMetrics] = useState<LiveMetrics>({
    activeSessions: 0,
    activeCarts: 0,
    activeCheckouts: 0,
    timestamp: new Date().toISOString(),
  });

  useEffect(() => {
    let cancelled = false;
    const fetchLiveMetrics = async () => {
      try {
        const response = await apiClient.get<any>('/analytics/live');
        if (!cancelled && response) {
          setMetrics(response);
        }
      } catch (err) {
        // Keep the last confirmed values; never replace analytics with simulated data.
      }
    };

    const interval = setInterval(fetchLiveMetrics, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const liveCities: any[] = [
    // { city: 'Nairobi, Kenya', visitors: 14, flag: '🇰🇪', status: 'Active Checkout' },
    // { city: 'Mombasa, Kenya', visitors: 5, flag: '🇰🇪', status: 'Browsing Store' },
    // { city: 'Kampala, Uganda', visitors: 3, flag: '🇺🇬', status: 'Added to Cart' },
    // { city: 'Dar es Salaam, TZ', visitors: 2, flag: '🇹🇿', status: 'Browsing Store' },
  ];

  return (
    <div className="space-y-5">
      {/* Live Banner Header */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-4 relative overflow-hidden shadow-sm">
        <div className="absolute right-0 top-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none -mr-20 -mt-20" />
        
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 relative z-10">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded bg-[#E3F1DF] border border-[#B3DEC1] text-[#006838] text-[10px] font-bold uppercase">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
              <span>Real-Time Store Pulse</span>
            </div>
            <h2 className="text-base font-semibold text-[#202223] dark:text-white">
              Live Store Activity
            </h2>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 max-w-xl">
              Current visitor sessions, active carts, and checkouts for your storefront.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 bg-[#F6F6F7] dark:bg-slate-900 border border-[#E1E4E8] dark:border-slate-800 rounded-lg px-4 py-3">
            <div>
              <div className="text-xs text-gray-400 font-medium">Right Now</div>
              <div className="text-xl font-bold font-mono text-[#006838]">
                {metrics.activeSessions}
              </div>
              <div className="text-[11px] text-[#5C5F62]">Active Visitors</div>
            </div>
            <div>
              <div className="text-xs text-gray-400 font-medium">In Carts</div>
              <div className="text-xl font-bold font-mono text-[#202223] dark:text-white">
                {metrics.activeCarts}
              </div>
              <div className="text-[11px] text-gray-400">Items Loaded</div>
            </div>
            <div>
              <div className="text-xs text-gray-400 font-medium">Checking Out</div>
              <div className="text-xl font-bold font-mono text-[#006838]">
                {metrics.activeCheckouts}
              </div>
              <div className="text-[11px] text-[#5C5F62]">Live Orders</div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid of Live Stage Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-3.5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              1. Active Browsing
            </span>
            <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 flex items-center justify-center">
              <Users weight="fill" className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-bold font-mono text-black dark:text-white mb-1">
            {metrics.activeSessions} sessions
          </div>
          <p className="text-xs text-gray-500">
            Visitors exploring product pages and catalog
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-3.5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              2. Cart Activity
            </span>
            <div className="w-8 h-8 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 flex items-center justify-center">
              <ShoppingCart weight="fill" className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-bold font-mono text-black dark:text-white mb-1">
            {metrics.activeCarts} carts
          </div>
          <p className="text-xs text-gray-500">
            Shoppers with items currently staged in bag
          </p>
        </div>

        <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-3.5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              3. Live Checkout
            </span>
            <div className="w-8 h-8 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-[#006838] flex items-center justify-center">
              <CreditCard weight="fill" className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-bold font-mono text-[#006838] dark:text-emerald-400 mb-1">
            {metrics.activeCheckouts} checkouts
          </div>
          <p className="text-xs text-gray-500">
            Currently entering delivery address & payment
          </p>
        </div>
      </div>

      {/* Regional Activity Table */}
      <div className="bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GlobeHemisphereWest weight="fill" className="w-5 h-5 text-[#006838]" />
            <h3 className="text-sm font-bold text-black dark:text-white">
              Live Regional Visitor Feed
            </h3>
          </div>
          <span className="text-xs text-gray-400 font-mono">Updated every 5s</span>
        </div>

        <div className="divide-y divide-gray-100 dark:divide-white/5">
          {liveCities.length > 0 ? (
            liveCities.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{item.flag}</span>
                  <div>
                    <div className="text-xs font-semibold text-black dark:text-white">
                      {item.city}
                    </div>
                    <div className="text-[11px] text-gray-400">{item.status}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-[#006838] dark:text-emerald-400 text-xs font-bold font-mono">
                    {item.visitors} active
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="py-8 text-center text-gray-400 text-xs italic">
              Awaiting geospatial streaming data (Coming Soon)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
